"use client";

import { useEffect, useRef, useState } from "react";

function getHljs() {
  return (window as unknown as Record<string, unknown>).hljs as
    | {
        registerLanguage: (name: string, fn: (h: unknown) => unknown) => void;
        getLanguage: (name: string) => unknown;
        highlight: (code: string, options: { language: string }) => { value: string };
      }
    | undefined;
}

let registered = false;

function ensureQosmRegistered() {
  const hljs = getHljs();
  if (!hljs) return null;
  if (!registered && !hljs.getLanguage("qosm")) {
    hljs.registerLanguage("qosm", (h: unknown) => {
      const hh = h as {
        COMMENT: (begin: string, end: string) => unknown;
        BACKSLASH_ESCAPE: unknown;
      };
      return {
        name: "Qosm",
        case_insensitive: false,
        contains: [
          hh.COMMENT("//", "$"),
          {
            scope: "string",
            begin: "`",
            end: "`",
            contains: [
              {
                scope: "subst",
                begin: "\\{\\{",
                end: "\\}\\}",
                contains: [
                  { scope: "variable", match: /[a-z][a-zA-Z0-9_]*/ },
                ],
              },
            ],
          },
          {
            scope: "string",
            begin: '"""',
            end: '"""',
            contains: [hh.BACKSLASH_ESCAPE],
          },
          {
            scope: "string",
            begin: '"',
            end: '"',
            contains: [hh.BACKSLASH_ESCAPE],
          },
          { scope: "number", match: /\b\d+\.\d+\b/ },
          { scope: "number", match: /\b\d+\b/ },
          { scope: "keyword", match: /[λ]/ },
          { scope: "literal", match: /∅/ },
          { scope: "type", match: /'[a-z][a-zA-Z0-9_]*/ },
          {
            scope: "keyword",
            match: /\b(let|in|match|with|type|effect|extern|lambda|rec|model)\b/,
          },
          { scope: "literal", match: /\b(true|false)\b/ },
          { scope: "type", match: /\b(Int|String|Bool|Float|Unit)\b/ },
          { scope: "title.class", match: /\b[A-Z][a-zA-Z0-9_]*\b/ },
          { scope: "property", match: /\.[a-z][a-zA-Z0-9_]*/ },
          { scope: "operator", match: /->/ },
          { scope: "operator", match: /\|>|>>|<<|==|!=|\+\+|\+|-|\*|\/|=|\||!/ },
          { scope: "punctuation", match: /[<>,;{}():@]/ },
        ],
      };
    });
    registered = true;
  }
  return hljs;
}

export default function QosmCode({
  code,
  className,
}: {
  code: string;
  className?: string;
}) {
  const [html, setHtml] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    function tryHighlight() {
      const hljs = ensureQosmRegistered();
      if (hljs) {
        const result = hljs.highlight(code, { language: "qosm" });
        setHtml(result.value);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        return true;
      }
      return false;
    }

    if (!tryHighlight()) {
      timerRef.current = setInterval(() => {
        tryHighlight();
      }, 100);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [code]);

  return (
    <pre className={className}>
      {html ? (
        <code
          className="hljs language-qosm font-mono"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <code className="font-mono">{code}</code>
      )}
    </pre>
  );
}
